import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { ProgressBar } from './ui'
import { LOCALE_KEY, LOCALES } from '../i18n'
import wordmark from '../assets/bonded-wordmark.webp'

/**
 * The app shell: ambient clouds, header, progress, and the animated page area.
 *
 * The entire screen transition is two things — `key={location.pathname}` on
 * <main>, which makes React remount it on every navigation, and the `.page-enter`
 * class, which replays one CSS keyframe. No animation library, same as the funnel.
 */
export function Layout({
	children,
	progress,
	accent,
	step,
	showBack,
	onBack,
	footer,
}: {
	children: ReactNode
	/** 0–1. Omitted on terminal screens, which have nothing left to progress through. */
	progress?: number
	/** The current section's colour token, so the bar changes hue between sections. */
	accent?: string
	/** "Question 4 of 11". Omitted on framing screens, which are not questions. */
	step?: { n: number; total: number }
	showBack?: boolean
	onBack?: () => void
	/**
	 * Rendered after <main>, as a sibling. Only the marketing landing page passes
	 * one — a survey screen is a single task and has nothing to put down there.
	 * It sits outside <main> on purpose: a <footer> nested inside it would be
	 * wrong semantically, and main's `justify-center` would drag it up into the
	 * middle of the screen alongside the content.
	 */
	footer?: ReactNode
}) {
	const { t, i18n } = useTranslation()
	const location = useLocation()

	const switchLanguage = (next: string) => {
		void i18n.changeLanguage(next)
		try {
			localStorage.setItem(LOCALE_KEY, next)
		} catch {
			// A locked-down or private context can refuse storage. The switch still
			// applies for this session; it just is not remembered.
		}
	}

	return (
		<div className="min-h-dvh flex flex-col">
			<div className="web-cloud-layer" aria-hidden>
				<div className="web-cloud web-cloud-one" />
				<div className="web-cloud web-cloud-two" />
				<div className="web-cloud web-cloud-three" />
			</div>

			<header className="lab-header flex items-center justify-between gap-3">
				<div className="header-side">
					{showBack && onBack ? (
						<button className="text-button" onClick={onBack} aria-label={t('back')}>
							← {t('back')}
						</button>
					) : null}
				</div>
				<img className="wordmark-logo" src={wordmark} alt={t('brand')} />
				<div className="header-side flex justify-end">
					{/* A native <select>: two options, and the OS picker beats any custom
					    dropdown on a phone. The visible label is the language's own name. */}
					<select
						className="language-select"
						aria-label={t('changeLanguage')}
						value={i18n.language.startsWith('el') ? 'el' : 'en'}
						onChange={(event) => switchLanguage(event.target.value)}
					>
						{LOCALES.map((locale) => (
							<option key={locale.value} value={locale.value}>
								{locale.label}
							</option>
						))}
					</select>
				</div>
			</header>

			{progress !== undefined ? (
				<div className="progress-shell">
					<ProgressBar value={progress * 100} accent={accent} />
					{step && step.n > 0 ? <p className="step-count">{t('stepOf', { n: step.n, total: step.total })}</p> : null}
				</div>
			) : null}

			<main key={location.pathname} className="page-enter flex flex-1 flex-col items-center justify-center px-5 py-8 sm:px-8">
				{children}
			</main>

			{footer}
		</div>
	)
}

export function ErrorPanel({ retry }: { retry: () => void }) {
	const { t } = useTranslation()
	return (
		<div className="content-column text-center">
			<h1 className="section-title">{t('errors.title')}</h1>
			<p className="body-copy">{t('errors.body')}</p>
			<button className="primary-button" onClick={retry}>
				{t('retry')}
			</button>
		</div>
	)
}

export function PageSkeleton() {
	return (
		<div className="content-column" aria-busy>
			<div className="skeleton skeleton-label" />
			<div className="skeleton skeleton-title" />
			<div className="skeleton skeleton-card" />
			<div className="skeleton skeleton-button" />
		</div>
	)
}
